import React from 'react';
import styled from 'styled-components';
import { Row, Col, Typography, Alert } from 'antd';
import { DashboardOutlined } from '@ant-design/icons';
import { DashboardLayout } from '../components/layout';
import { PlatformStatusBar } from '../components/common';
import {
  M365Widget,
  SecurityWidget,
  BackupWidget,
  DattoWidget,
  DattoSaasWidget,
  ServiceDeskWidget,
  AssetsWidget,
} from '../components/widgets';
import { useTenant } from '../context/TenantContext';
import { useAuth } from '../context/AuthContext';
import { ROLES } from '../constants/roles';
import { useGatewayHealth, useCustomerPlatformStatus, useSyncCustomerPlatforms } from '../hooks';
import { message } from 'antd';
import { cippTenantIdOf, isM365Managed } from '../utils/customerKey';

const { Title, Text } = Typography;

const PageHeader = styled.div`
  margin-bottom: ${({ theme }) => theme.spacing.xl};
`;

const ConnectionAlert = styled(Alert)`
  margin-bottom: ${({ theme }) => theme.spacing.lg};
`;

const SectionTitle = styled(Title)`
  color: ${({ theme }) => theme.colors.text} !important;
  margin-top: ${({ theme }) => theme.spacing.xl} !important;
  margin-bottom: ${({ theme }) => theme.spacing.lg} !important;
`;

const DashboardPage = () => {
  const { selectedTenant, isAllTenantsView } = useTenant();
  const { hasAnyRole } = useAuth();

  // Check gateway connectivity
  const {
    data: gatewayHealth,
    isLoading: gatewayLoading,
    isError: gatewayError,
  } = useGatewayHealth();

  // Get platform configuration status for selected tenant
  const {
    platforms,
    configuredCount,
    totalPlatforms,
    isMapped,
    displayName: mappedDisplayName,
    isLoading: platformStatusLoading,
  } = useCustomerPlatformStatus(selectedTenant?.id);

  // Sync mutation for discovering platform IDs
  const syncMutation = useSyncCustomerPlatforms();

  // Handle sync button click
  const handleSync = ({ cippTenantId, displayName }) => {
    syncMutation.mutate(
      { cippTenantId, displayName },
      {
        onSuccess: (data) => {
          if (data?.alreadyMapped) {
            message.info('Customer is already mapped');
          } else if (data?.platformsDiscovered > 0) {
            message.success(
              `Discovered ${data.platformsDiscovered} of ${data.totalPlatforms} platforms`
            );
          } else {
            message.warning('No matching platforms found');
          }
        },
        onError: (error) => {
          message.error(`Sync failed: ${error.message || 'Unknown error'}`);
        },
      }
    );
  };

  // Show connection status if gateway is not connected
  // Gateway returns status: "healthy" when connected
  const isConnected = gatewayHealth?.status === 'healthy';

  return (
    <DashboardLayout>
      <PageHeader>
        <Title level={2} style={{ color: 'var(--odin-text-primary)', marginBottom: 8 }}>
          <DashboardOutlined style={{ marginRight: 12, color: 'var(--odin-accent)' }} />
          {isAllTenantsView ? 'Dashboard Overview' : selectedTenant?.displayName || 'Dashboard'}
        </Title>
        <Text style={{ color: 'var(--odin-text-secondary)' }}>
          {isAllTenantsView
            ? 'Unified MSP Management Platform - All Customers'
            : `Customer Overview - ${selectedTenant?.displayName || ''}`}
        </Text>
      </PageHeader>

      {/* Platform Status Bar - shows when specific tenant selected */}
      {!isAllTenantsView && selectedTenant?.id && (
        <PlatformStatusBar
          platforms={platforms}
          configuredCount={configuredCount}
          totalPlatforms={totalPlatforms}
          isMapped={isMapped}
          displayName={mappedDisplayName || selectedTenant?.displayName}
          loading={platformStatusLoading}
          onSync={handleSync}
          isSyncing={syncMutation.isPending}
          cippTenantId={cippTenantIdOf(selectedTenant)}
          syncError={syncMutation.error?.message}
          canSync={hasAnyRole([ROLES.ADMIN])}
          m365Managed={isM365Managed(selectedTenant)}
        />
      )}

      {/* Connection Status */}
      {!gatewayLoading && (gatewayError || !isConnected) && (
        <ConnectionAlert
          type="warning"
          message="Gateway Connection"
          description="Unable to connect to CSG Aggregation Gateway. Displaying placeholder data. Configure VITE_CSG_GATEWAY_URL in your .env file."
          showIcon
        />
      )}

      {/* Platform Widgets */}
      <SectionTitle level={4}>Platform Overview</SectionTitle>
      <Row gutter={[24, 24]}>
        <Col xs={24} lg={12}>
          <M365Widget />
        </Col>
        <Col xs={24} lg={12}>
          <SecurityWidget />
        </Col>
        <Col xs={24} lg={12}>
          <BackupWidget />
        </Col>
        <Col xs={24} lg={12}>
          <DattoWidget />
        </Col>
        <Col xs={24} lg={12}>
          <ServiceDeskWidget />
        </Col>
        <Col xs={24} lg={12}>
          <DattoSaasWidget />
        </Col>
        <Col xs={24}>
          <AssetsWidget />
        </Col>
      </Row>
    </DashboardLayout>
  );
};

export default DashboardPage;
